/**
 * Block picker / section library - NOS-26.
 *
 * Modal UI to select a block type to add. Shows grid of block types
 * with icon and label.
 */

import {
  Layout,
  FileText,
  BarChart3,
  Heart,
  Users,
  Footprints,
  Type,
  Heading,
  Image,
  Clock,
  FormInput,
  List,
  Quote,
  Video,
  MapPin,
  Download,
  Code2,
  Mail,
  PanelTop,
  Megaphone,
  type LucideIcon,
} from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { BlockType } from "./blocks/types"
import type { BlockIconName } from "./blocks/registry"
import { getBlockPickerTypes, getBlockConfig } from "./blocks/registry"

const ICON_MAP: Record<BlockIconName, LucideIcon> = {
  Layout,
  FileText,
  BarChart3,
  Heart,
  Users,
  Footprints,
  Type,
  Heading,
  Image,
  Clock,
  FormInput,
  List,
  Quote,
  Video,
  MapPin,
  Download,
  Code2,
  Mail,
  PanelTop,
  Megaphone,
}

export interface BlockPickerProps {
  open: boolean
  onClose: () => void
  onSelect: (type: BlockType) => void
}

export function BlockPicker({ open, onClose, onSelect }: BlockPickerProps) {
  const types = getBlockPickerTypes()

  const handleSelect = (type: BlockType) => {
    onSelect(type)
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add block</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 pt-2">
          {types.map((type) => {
            const config = getBlockConfig(type)
            const Icon = ICON_MAP[config.icon] ?? FileText
            return (
              <button
                key={type}
                type="button"
                className="flex flex-col items-center gap-2 rounded-lg border bg-background p-4 text-left transition-colors hover:bg-muted hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-ring"
                onClick={() => handleSelect(type)}
              >
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-md bg-muted"
                  style={{ color: "var(--primary-color, #3b82f6)" }}
                >
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </div>
                <span className="text-sm font-medium">{config.label}</span>
              </button>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}
